import { compareVersions } from './appUpdate';
import { authService } from './auth';
import type { LanguageType } from './i18n';

export interface ConnectorAuthField {
  key: string;
  label?: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  help?: string;
}

export interface ConnectorCatalogItem {
  id: number;
  slug: string;
  display_name: string;
  summary?: string;
  description?: string;
  provider?: string;
  category?: string;
  icon_url?: string;
  auth_type: 'none' | 'token' | 'oauth' | 'qrcode' | 'im' | string;
  auth_schema?: string;
  oauth_ready?: boolean;
  /** 平台托管方式、配置版本及授权状态；旧平台缺省时沿用兼容路径。 */
  access_mode?: 'platform_proxy' | 'client_direct' | string;
  flow_type?: string;
  config_error?: string;
  config_revision?: number;
  proxy_supported?: boolean;
  client_min_version?: string;
  reauth_required?: boolean;
  is_featured?: boolean;
  owner_user_id?: number;
  connector_type?: 'mcp' | 'im' | string;
}

let catalogSnapshot: ConnectorCatalogItem[] | null = null;
let catalogSnapshotLanguage: LanguageType | null = null;
/** 连接存在与凭据有效分开建模；重授权连接仍允许主动断开。 */
export interface ConnectorConnectionState { reauthRequired: boolean }
let connectedSnapshot: Map<string, ConnectorConnectionState> | null = null;
let catalogGeneration = 0;
let connectionGeneration = 0;

/** 深复制状态，防止页面修改服务缓存；凭据始终只由 Platform 持有。 */
function cloneConnections(value: Map<string, ConnectorConnectionState>): Map<string, ConnectorConnectionState> {
  return new Map([...value].map(([slug, state]) => [slug, { ...state }]));
}

/** 兼容旧调用者：只有不要求重授权的连接才能用于工具选择。 */
function availableSlugs(value: Map<string, ConnectorConnectionState>): Set<string> {
  return new Set([...value].filter(([, state]) => !state.reauthRequired).map(([slug]) => slug));
}
let snapshotIdentity: string | null = null;
/** 账号、平台和登录周期共同限定缓存；身份标记只留内存。 */
function currentIdentity(): string | null {
  return authService.getSessionIdentity();
}
/** 读取前清除其他用户的目录和连接状态，防止注销后或换账号串数据。 */
function ensureSnapshotIdentity(): string | null {
  const identity = currentIdentity();
  if (identity !== snapshotIdentity) {
    snapshotIdentity = identity; catalogSnapshot = null; catalogSnapshotLanguage = null; connectedSnapshot = null;
  }
  return identity;
}


function buildHeaders(): Record<string, string> | null {
  const auth = authService.getAuthState();
  if (!auth) return null;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth.authMethod === 'bearer' && auth.token) headers.Authorization = `Bearer ${auth.token}`;
  if (auth.userIdHeader) headers['New-Api-User'] = auth.userIdHeader;
  return headers;
}

async function apiFetch(path: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', body?: unknown): Promise<any> {
  const identity = ensureSnapshotIdentity();
  const baseUrl = authService.getBaseUrl();
  const headers = buildHeaders();
  if (!baseUrl || !headers) throw new Error('Authentication is unavailable');
  const response = await window.electron.api.fetch({
    url: `${baseUrl}${path}`,
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (currentIdentity() !== identity) throw new Error('Account changed');
  if (!response?.ok) {
    throw new Error(response?.status ? `HTTP ${response.status}` : response?.error || 'Network error');
  }
  return response.data;
}

export function getConnectorCatalogSnapshot(language?: LanguageType): ConnectorCatalogItem[] | null {
  ensureSnapshotIdentity();
  if (language && catalogSnapshotLanguage !== language) return null;
  return catalogSnapshot ? [...catalogSnapshot] : null;
}

export function getConnectorConnectionsSnapshot(): Set<string> | null {
  ensureSnapshotIdentity();
  return connectedSnapshot ? availableSlugs(connectedSnapshot) : null;
}

/** 返回全部连接状态供设置页区分已连接与待重授权。 */
export function getConnectorConnectionStatesSnapshot(): Map<string, ConnectorConnectionState> | null {
  ensureSnapshotIdentity();
  return connectedSnapshot ? cloneConnections(connectedSnapshot) : null;
}

export async function listConnectorCatalog(language: LanguageType): Promise<ConnectorCatalogItem[]> {
  const generation = ++catalogGeneration;
  const identity = ensureSnapshotIdentity();
  const query = new URLSearchParams({ limit: '100', lang: language });
  const data = await apiFetch(`/api/connector-catalog/connectors?${query.toString()}`, 'GET');
  const items = data?.data?.items;
  if (!data?.success || !Array.isArray(items)) throw new Error('Invalid connector catalog response');
  if (currentIdentity() !== identity) throw new Error('Account changed');
  if (generation !== catalogGeneration) throw new Error('Connector catalog changed');
  const previous = new Map(catalogSnapshot?.map(item => [item.slug, item.config_revision]));
  if (catalogSnapshot && (items.length !== catalogSnapshot.length || items.some((item: ConnectorCatalogItem) =>
    !previous.has(item.slug) || previous.get(item.slug) !== item.config_revision))) {
    connectedSnapshot = null;
    connectionGeneration++;
  }
  catalogSnapshot = items;
  catalogSnapshotLanguage = language;
  return [...items];
}

/** 读取权威授权状态，只有明确 true 才视为需要重授权，兼容旧平台。 */
export async function listMyConnectorConnections(): Promise<Map<string, ConnectorConnectionState>> {
  const generation = ++connectionGeneration;
  const identity = ensureSnapshotIdentity();
  const data = await apiFetch('/api/connector/my', 'GET');
  const items = data?.data?.items;
  if (!data?.success || !Array.isArray(items)) throw new Error('Invalid connector connection response');
  if (currentIdentity() !== identity) throw new Error('Account changed');
  if (generation !== connectionGeneration) throw new Error('Connector connections changed');
  connectedSnapshot = new Map(items.filter((item: ConnectorCatalogItem) => typeof item?.slug === 'string' && item.slug)
    .map((item: ConnectorCatalogItem) => [item.slug, { reauthRequired: item.reauth_required === true }]));
  return cloneConnections(connectedSnapshot);
}

/** 工具选择仍使用原来的 Set 契约，但不选择已失效凭据。 */
export async function listMyConnectedSlugs(): Promise<Set<string>> {
  return availableSlugs(await listMyConnectorConnections());
}

export async function authorizeConnector(
  slug: string,
  values: Record<string, string>,
): Promise<{ success: boolean; message?: string; authorizeUrl?: string }> {
  const data = await apiFetch(`/api/connector/authorize/${encodeURIComponent(slug)}`, 'POST', values);
  if (data?.success) { connectedSnapshot = null; connectionGeneration++; }
  return {
    success: Boolean(data?.success),
    message: data?.message,
    authorizeUrl: data?.data?.authorize_url,
  };
}

export async function disconnectConnector(slug: string): Promise<{ success: boolean; message?: string }> {
  const data = await apiFetch(`/api/connector/disconnect/${encodeURIComponent(slug)}`, 'DELETE');
  if (data?.success) { connectedSnapshot = null; connectionGeneration++; }
  return { success: Boolean(data?.success), message: data?.message };
}

/** Owner decision 2026-09-28 (Q4): the Web runtime supports http / sse (streamable-http) MCP only. */
export const WEB_MCP_STDIO_UNSUPPORTED_MESSAGE =
  'Web 端暂不支持 stdio 类型的 MCP（需要在服务器上执行本地命令）。请改用 http / sse（streamable-http）类型的 MCP 服务地址。';

export type CustomMcpTransport = 'streamable-http' | 'sse' | 'stdio';

export async function createCustomMcpConnector(input: {
  name: string;
  url: string;
  transport: CustomMcpTransport;
  token?: string;
}): Promise<{ success: boolean; message?: string }> {
  if (input.transport === 'stdio') throw new Error(WEB_MCP_STDIO_UNSUPPORTED_MESSAGE);
  const url = input.url.trim();
  if (!/^https?:\/\//i.test(url)) {
    throw new Error(`MCP 地址必须是 http:// 或 https:// 开头的网址。${WEB_MCP_STDIO_UNSUPPORTED_MESSAGE}`);
  }
  const data = await apiFetch('/api/connector/custom', 'POST', {
    display_name: input.name.trim(),
    mcp_url: url,
    mcp_transport: input.transport,
    ...(input.token?.trim() ? { token: input.token.trim() } : {}),
  });
  if (data?.success) {
    connectedSnapshot = null;
    catalogSnapshot = null;
  }
  return { success: Boolean(data?.success), message: data?.message };
}

export async function deleteCustomMcpConnector(slug: string): Promise<{ success: boolean; message?: string }> {
  const data = await apiFetch(`/api/connector/custom/${encodeURIComponent(slug)}`, 'DELETE');
  if (data?.success) {
    connectedSnapshot = null;
    catalogSnapshot = null;
  }
  return { success: Boolean(data?.success), message: data?.message };
}

export function parseAuthFields(schema?: string): ConnectorAuthField[] {
  if (!schema) return [];
  try {
    const parsed = JSON.parse(schema);
    return Array.isArray(parsed?.fields) ? parsed.fields : [];
  } catch {
    return [];
  }
}

export function openExternalUrl(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** 读取本人的可编辑配置；平台从不回传明文令牌。 */
export async function getCustomMcpConnector(slug: string): Promise<{ name: string; url: string; transport: CustomMcpTransport; token: string }> {
  const response = await apiFetch(`/api/connector/custom/${encodeURIComponent(slug)}`, 'GET');
  const data = response?.data;
  if (!response?.success || typeof data?.display_name !== 'string' || typeof data?.mcp_url !== 'string'
    || !['sse', 'streamable-http'].includes(data.mcp_transport)) throw new Error('Invalid custom connector');
  return { name: data.display_name, url: data.mcp_url, transport: data.mcp_transport, token: '' };
}

/** 修改自定义 MCP 保留 slug；地址变化且未填新令牌时平台清除旧凭据。 */
export async function updateCustomMcpConnector(slug: string, input: { name: string; url: string; transport: CustomMcpTransport; token?: string }): Promise<{ success: boolean; message?: string }> {
  if (input.transport === 'stdio') throw new Error(WEB_MCP_STDIO_UNSUPPORTED_MESSAGE);
  const data = await apiFetch(`/api/connector/custom/${encodeURIComponent(slug)}`, 'PUT', {
    display_name: input.name.trim(), mcp_url: input.url.trim(), mcp_transport: input.transport,
    ...(input.token?.trim() ? { token: input.token.trim() } : {}),
  });
  if (data?.success) { catalogSnapshot = null; connectedSnapshot = null; catalogGeneration++; connectionGeneration++; }
  return { success: Boolean(data?.success), message: data?.message };
}

/** Web 仅承载平台代理连接；显式直连/不完整配置不误入普通授权。 */
export function connectorUnavailableReason(item: ConnectorCatalogItem, version: string): string | null {
  if (item.client_min_version && (!version || compareVersions(version, item.client_min_version) < 0)) return 'connectorClientUpgradeRequired';
  if (item.connector_type === 'im') return null;
  if (item.access_mode === 'client_direct' || (!item.access_mode && item.proxy_supported === false)) return 'connectorDesktopOnly';
  if (item.config_error && (item.config_error === 'direct_flow_incomplete' || parseAuthFields(item.auth_schema).length === 0)) return 'connectorConfigIncomplete';
  return null;
}
